import { useEffect, useMemo } from "react"
import {
    TemplateName,
    TemplateSlug,
    nameToSlug
} from "../Models/API/AppTemplateModel"
import { MarketplaceTemplateData } from "../Models/Client/Marketplace"
import { Result } from "../Models/Utility/Result"
import { toAPIAppTemplateModel } from "../Models/External/Contentful"
import { FetchState, hasResult, hasValue } from "../Models/Client/FetchState"
import { useTemplateFromList } from "./useTemplateFromList"
import { useAppState } from "./useReduxState"
import { useDispatch } from "react-redux"
import { getDataProgram } from "../Actions/External/ContentfulActions"

export function useMarketplaceTemplate(
    templateName?: TemplateName,
    templateSlug?: TemplateSlug
): FetchState<MarketplaceTemplateData> {
    const dispatch = useDispatch()
    const slug =
        templateSlug === undefined
            ? templateName === undefined
                ? undefined
                : nameToSlug(templateName)
            : templateSlug
    const templateFetch = useTemplateFromList(templateName, templateSlug)
    const seoTemplateFetch = useAppState(state =>
        slug === undefined
            ? undefined
            : state.external.contentful.dataPrograms.records.get(slug)
    )

    useEffect(() => {
        if (
            slug !== undefined &&
            hasResult(templateFetch) &&
            templateFetch.isError
        ) {
            dispatch(getDataProgram(slug))
        }
    }, [dispatch, slug, templateFetch])

    return useMemo<FetchState<MarketplaceTemplateData>>(() => {
        if (hasValue(templateFetch)) {
            return Result.ok({
                type: "implemented",
                template: templateFetch.value
            })
        }

        if (hasValue(seoTemplateFetch)) {
            return Result.ok({
                type: "cms",
                cmsData: seoTemplateFetch.value,
                template: toAPIAppTemplateModel(seoTemplateFetch.value)
            })
        }

        if (
            hasResult(templateFetch) &&
            templateFetch.isError &&
            hasResult(seoTemplateFetch) &&
            seoTemplateFetch.isError
        ) {
            return Result.error("Not found")
        }
    }, [seoTemplateFetch, templateFetch])
}
